import type { Arcana, CardId, Deck, Suit } from './types.js';
import { SUIT_ORDER } from './types.js';
import { courtRankFor } from './vocab.js';

/** Card ids are `<suit>-<number>`, matching `tarot-data.js` and the img/ tree. */
export function parseCardId(id: CardId): { suit: Suit; number: number } {
  const idx = id.lastIndexOf('-');
  const suit = id.slice(0, idx) as Suit;
  const number = Number(id.slice(idx + 1));

  if (!SUIT_ORDER.includes(suit) || Number.isNaN(number)) {
    throw new Error(`Unrecognised card id: ${id}`);
  }
  return { suit, number };
}

export function makeCardId(suit: Suit, number: number): CardId {
  return `${suit}-${number}`;
}

export function arcanaFor(suit: Suit): Arcana {
  return suit === 'major' ? 'major' : 'minor';
}

/**
 * Position in the canonical browse order: Major, Wands, Cups, Swords, Coins.
 * Suits are spaced 100 apart so a deck can insert cards (Minchiate's zodiac,
 * for instance) without renumbering everything after them.
 */
export function canonicalSortOrder(suit: Suit, number: number): number {
  return SUIT_ORDER.indexOf(suit) * 100 + number;
}

export function cardImagePath(deckSlug: string, suit: Suit, number: number): string {
  return `/cards/${deckSlug}/${suit}/${number}.webp`;
}

/**
 * Seed decks. Only `rws` is playable today; the rest exist so the deck
 * selector and the "alternate art" grid render the real structure rather
 * than a hardcoded mock. They carry placeholder art until real scans land.
 */
export const SEED_DECKS: Deck[] = [
  {
    slug: 'rws',
    type: 'tarot78',
    baseDeckSlug: null,
    name: { en: 'Smith-Waite-Rider', es: 'Smith-Waite-Rider' },
    blurb: {
      en: 'The classic 78-card deck. Every meaning in 3ojo is written against this one.',
      es: 'La baraja clásica de 78 cartas. Todos los significados de 3ojo parten de ella.',
    },
    status: 'available',
    minTier: 'guest',
    coverImage: '/decks/rws-cover.webp',
    cardBackImage: '/decks/back.webp',
  },
  {
    slug: 'osho',
    type: 'oracle',
    baseDeckSlug: null,
    name: { en: 'Osho Zen', es: 'Osho Zen' },
    blurb: {
      en: 'A Zen deck that rhymes with the 78 without copying it.',
      es: 'Una baraja Zen que dialoga con las 78 sin copiarlas.',
    },
    status: 'coming-soon',
    minTier: 'mystic',
    coverImage: '/decks/osho-cover.webp',
    cardBackImage: '/decks/back.webp',
  },
  {
    slug: 'art-nouveau',
    type: 'art-variant',
    baseDeckSlug: 'rws',
    name: { en: 'Art Nouveau', es: 'Art Nouveau' },
    blurb: {
      en: 'The same 78 meanings, redrawn in flowing line and gold.',
      es: 'Los mismos 78 significados, redibujados en línea y oro.',
    },
    status: 'coming-soon',
    minTier: 'mystic',
    coverImage: '/decks/placeholder-cover.webp',
    cardBackImage: '/decks/back.webp',
  },
  {
    slug: 'thoth',
    type: 'art-variant',
    baseDeckSlug: 'rws',
    name: { en: 'Thoth', es: 'Thoth' },
    blurb: {
      en: 'The same 78 meanings, through a sharper esoteric lens.',
      es: 'Los mismos 78 significados, con una mirada esotérica más afilada.',
    },
    status: 'coming-soon',
    minTier: 'mystic',
    coverImage: '/decks/placeholder-cover.webp',
    cardBackImage: '/decks/back.webp',
  },
];

export const DEFAULT_DECK_SLUG = 'rws';
/** Opening card when a user has no saved position, per SPEC. */
export const DEFAULT_CARD_ID: CardId = 'major-18'; // The Moon

export { courtRankFor };
